import React, { useState } from 'react';
import {
  Clock,
  CheckCircle2,
  Calendar,
  User,
  Plus,
  AlertCircle,
  Briefcase,
  ChevronRight,
  Filter,
  Check
} from 'lucide-react';
import { FollowUpItem, PracticeClient, PracticeCase } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';

interface FollowUpCentreProps {
  followUps: FollowUpItem[];
  clients: PracticeClient[];
  cases: PracticeCase[];
  lang: Language;
  onCompleteFollowUp: (id: string, notes?: string) => void;
  onOpenCaseWorkspace: (caseItem: PracticeCase) => void;
  onOpenClientProfile: (client: PracticeClient) => void;
}

export const FollowUpCentre: React.FC<FollowUpCentreProps> = ({
  followUps,
  clients,
  cases,
  lang,
  onCompleteFollowUp,
  onOpenCaseWorkspace,
  onOpenClientProfile
}) => {
  const isHi = lang === 'hi';
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'OVERDUE' | 'COMPLETED'>('PENDING');

  const filteredItems = followUps.filter((item) => {
    if (filter === 'PENDING') return item.status === 'PENDING';
    if (filter === 'OVERDUE') return item.status === 'OVERDUE';
    if (filter === 'COMPLETED') return item.status === 'COMPLETED';
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Top Header */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-black text-astro-text dark:text-astro-muted">
              {isHi ? 'फॉलो-अप एवं क्लाइंट संवाद केंद्र' : 'Client Follow-Up & Milestone Hub'}
            </h2>
            <p className="text-xs text-astro-muted">
              {isHi
                ? 'समयबद्ध समीक्षाएं, उपाय अनुपालन एवं आगामी परामर्श स्मरण'
                : 'Track check-ins, remedy adherence, and timing milestone reviews'}
            </p>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 bg-astro-secondary-surface dark:bg-astro-secondary p-1 rounded-lg border border-astro-border dark:border-astro-strong-border">
          {[
            { id: 'PENDING', labelEn: 'Pending / Due', labelHi: 'लंबित' },
            { id: 'OVERDUE', labelEn: 'Overdue', labelHi: 'अतिदेय' },
            { id: 'COMPLETED', labelEn: 'Completed', labelHi: 'संपन्न' },
            { id: 'ALL', labelEn: 'All Items', labelHi: 'सभी' }
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id as any)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                filter === f.id
                  ? 'bg-white dark:bg-astro-secondary text-astro-secondary dark:text-amber-200 shadow-xs'
                  : 'text-astro-muted hover:text-astro-text dark:hover:text-astro-muted'
              }`}
            >
              {isHi ? f.labelHi : f.labelEn}
            </button>
          ))}
        </div>
      </div>

      {/* Follow-Up Items List */}
      <div className="space-y-3">
        {filteredItems.map((item) => {
          const client = clients.find((c) => c.id === item.clientId);
          const relatedCase = cases.find((c) => c.id === item.caseId);

          return (
            <div
              key={item.id}
              className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
              <div className="space-y-1.5 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm text-astro-text dark:text-astro-muted">
                    {item.clientName}
                  </span>
                  <span className="font-mono text-xs text-astro-muted">
                    ({item.clientRefId})
                  </span>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      item.status === 'OVERDUE'
                        ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30'
                        : item.status === 'COMPLETED'
                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                        : 'bg-astro-secondary-surface0/15 text-astro-secondary dark:text-amber-300'
                    }`}
                  >
                    {item.status}
                  </span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                    {item.category}
                  </span>
                </div>

                <p className="text-xs text-astro-secondary-text dark:text-astro-muted">
                  {item.reason}
                </p>

                <div className="flex items-center gap-4 text-xs text-astro-muted pt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-astro-muted" />
                    <strong>Due Date:</strong> {item.dueDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5 text-astro-muted" />
                    {item.assignedPractitioner}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 shrink-0">
                {item.status !== 'COMPLETED' && (
                  <button
                    onClick={() => onCompleteFollowUp(item.id)}
                    className="min-h-[36px] px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>{isHi ? 'पूर्ण चिह्नित करें' : 'Mark Done'}</span>
                  </button>
                )}

                {relatedCase && (
                  <button
                    onClick={() => onOpenCaseWorkspace(relatedCase)}
                    className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-card text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer border border-astro-border dark:border-astro-strong-border"
                    title="View Case"
                  >
                    <Briefcase className="w-4 h-4" />
                  </button>
                )}

                {client && (
                  <button
                    onClick={() => onOpenClientProfile(client)}
                    className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-card text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer border border-astro-border dark:border-astro-strong-border"
                    title="View Client"
                  >
                    <User className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {filteredItems.length === 0 && (
          <div className="p-12 text-center bg-white dark:bg-slate-850 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/80 text-xs text-astro-muted">
            No follow-up tasks in this category.
          </div>
        )}
      </div>
    </div>
  );
};
